<script setup>
// Painel "Detalhes da execução" da tela Testar (Fase 7, tela 06): o que o run deixou rastreável — cenário (o agente
// que respondeu), tools chamadas, dados coletados N/M (Polaris::DataCollection), desfecho (a tool set_outcome,
// inclusive o erro dela) e o perfil pré-carregado. O que o run não registra, o painel não inventa.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Badge } from 'dashboard/components-next/polaris/ui/badge';
import { prettyToolName } from './runDetailsFormat';

const props = defineProps({
  runDetails: { type: Object, default: null },
});

const { t } = useI18n();

const tools = computed(() =>
  (props.runDetails?.tools_called || []).map(prettyToolName)
);

const collected = computed(() => {
  const items = props.runDetails?.data_collection || [];
  return {
    done: items.filter(item => item.collected).length,
    total: items.length,
    missing: items.filter(item => !item.collected).map(item => item.key),
  };
});

const outcome = computed(() => props.runDetails?.outcome || null);
const scenario = computed(() => props.runDetails?.scenario || null);
const profile = computed(() => props.runDetails?.customer_profile || null);
</script>

<template>
  <aside
    class="flex w-80 shrink-0 flex-col gap-3 rounded-polaris-xl border border-solid border-polaris-border bg-polaris-card p-4"
    data-testid="playground-run-details"
  >
    <div>
      <h3 class="text-sm font-medium text-polaris-foreground">
        {{ t('POLARIS.PLAYGROUND.RUN_DETAILS.TITLE') }}
      </h3>
      <p class="text-xs text-polaris-muted-foreground">
        {{ t('POLARIS.PLAYGROUND.RUN_DETAILS.SUBTITLE') }}
      </p>
    </div>

    <p v-if="!runDetails" class="text-xs text-polaris-muted-foreground">
      {{ t('POLARIS.PLAYGROUND.RUN_DETAILS.EMPTY') }}
    </p>

    <template v-else>
      <div class="flex items-start justify-between gap-2 text-sm">
        <span class="text-polaris-muted-foreground">
          {{ t('POLARIS.PLAYGROUND.RUN_DETAILS.SCENARIO') }}
        </span>
        <span class="text-right text-polaris-foreground">
          {{ scenario?.title || scenario?.agent_name || '—' }}
        </span>
      </div>

      <div class="flex items-start justify-between gap-2 text-sm">
        <span class="text-polaris-muted-foreground">
          {{ t('POLARIS.PLAYGROUND.RUN_DETAILS.DATA_COLLECTED') }}
        </span>
        <span class="text-right text-polaris-foreground">
          <template v-if="collected.total">
            {{ collected.done }}/{{ collected.total }}
            <span
              v-if="collected.missing.length"
              class="block text-xs text-polaris-muted-foreground"
            >
              {{
                t('POLARIS.PLAYGROUND.RUN_DETAILS.MISSING', {
                  keys: collected.missing.join(', '),
                })
              }}
            </span>
          </template>
          <template v-else>—</template>
        </span>
      </div>

      <div class="flex items-start justify-between gap-2 text-sm">
        <span class="text-polaris-muted-foreground">
          {{ t('POLARIS.PLAYGROUND.RUN_DETAILS.OUTCOME') }}
        </span>
        <span class="text-right">
          <Badge v-if="outcome?.status === 'ok'" variant="success">
            {{ outcome.title || outcome.key }}
          </Badge>
          <Badge v-else-if="outcome" variant="warning">
            {{ t('POLARIS.PLAYGROUND.RUN_DETAILS.OUTCOME_ERROR') }}
          </Badge>
          <span v-else class="text-polaris-muted-foreground">—</span>
        </span>
      </div>

      <div v-if="tools.length" class="flex flex-col gap-1 text-sm">
        <span class="text-polaris-muted-foreground">
          {{ t('POLARIS.PLAYGROUND.RUN_DETAILS.TOOLS') }}
        </span>
        <div class="flex flex-wrap gap-1">
          <Badge v-for="tool in tools" :key="tool" variant="outline">
            {{ tool }}
          </Badge>
        </div>
      </div>

      <div v-if="profile" class="flex flex-col gap-1 text-sm">
        <span class="text-polaris-muted-foreground">
          {{ t('POLARIS.PLAYGROUND.RUN_DETAILS.CUSTOMER_PROFILE') }}
        </span>
        <p
          class="whitespace-pre-wrap rounded-md bg-polaris-accent p-2 font-mono text-xs text-polaris-foreground"
        >
          {{ profile }}
        </p>
      </div>
    </template>
  </aside>
</template>
